import ReactJson, { type InteractionProps } from "@microlink/react-json-view"
import { useIslandProps } from "@archipelago-js/react"
import React, { useState, useCallback, useRef, useEffect } from "react"

const THEME = {
  base00: "#ffffff",
  base01: "#f5f5f5",
  base02: "#e0e0e0",
  base03: "#9e9e9e",
  base04: "#6b7280",
  base05: "#374151",
  base06: "#1f2937",
  base07: "#111827",
  base08: "#ef4444",
  base09: "#f97316",
  base0A: "#eab308",
  base0B: "#22c55e",
  base0C: "#06b6d4",
  base0D: "#6366f1",
  base0E: "#a855f7",
  base0F: "#ec4899",
}

function dispatchBodyChange(el: HTMLElement, body: string, changed: boolean) {
  el.dispatchEvent(
    new CustomEvent("replay-body:changed", {
      bubbles: true,
      detail: { body, changed },
    })
  )
}

export default function ReplayBodyEditor() {
  const { props } = useIslandProps()
  const isJson = props.isJson as boolean
  const originalBody = (props.body as string) ?? ""

  const containerRef = useRef<HTMLDivElement>(null)

  if (isJson) {
    return (
      <JsonEditor
        originalBody={originalBody}
        containerRef={containerRef}
      />
    )
  }

  return (
    <TextEditor
      originalBody={originalBody}
      containerRef={containerRef}
    />
  )
}

function JsonEditor({
  originalBody,
  containerRef,
}: {
  originalBody: string
  containerRef: React.RefObject<HTMLDivElement | null>
}) {
  const parsed = React.useMemo(() => {
    try {
      return JSON.parse(originalBody)
    } catch {
      return null
    }
  }, [originalBody])

  const [src, setSrc] = useState<object>(parsed ?? {})
  const [rawMode, setRawMode] = useState(parsed === null)
  const [rawValue, setRawValue] = useState(originalBody)
  const [jsonError, setJsonError] = useState<string | null>(null)

  useEffect(() => {
    if (!containerRef.current) return
    const body = rawMode ? rawValue : JSON.stringify(src)
    const changed = body !== originalBody
    dispatchBodyChange(containerRef.current, body, changed)
  }, [src, rawValue, rawMode, originalBody, containerRef])

  const handleEdit = useCallback((edit: InteractionProps) => {
    setSrc(edit.updated_src)
  }, [])

  const handleAdd = useCallback((add: InteractionProps) => {
    setSrc(add.updated_src)
  }, [])

  const handleDelete = useCallback((del: InteractionProps) => {
    setSrc(del.updated_src)
  }, [])

  const toggleRawMode = useCallback(() => {
    if (rawMode) {
      try {
        const parsed = JSON.parse(rawValue)
        setSrc(parsed)
        setJsonError(null)
        setRawMode(false)
      } catch (e: unknown) {
        setJsonError(`Invalid JSON: ${(e as Error).message}`)
      }
    } else {
      setRawValue(JSON.stringify(src, null, 2))
      setJsonError(null)
      setRawMode(true)
    }
  }, [rawMode, rawValue, src])

  return (
    <div ref={containerRef} className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">JSON validated</span>
        <button
          type="button"
          onClick={toggleRawMode}
          className="text-xs text-indigo-600 hover:text-indigo-800"
        >
          {rawMode ? "Tree View" : "Raw JSON"}
        </button>
      </div>

      {rawMode ? (
        <>
          <textarea
            className="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs font-mono"
            rows={12}
            value={rawValue}
            onChange={(e) => {
              setRawValue(e.target.value)
              setJsonError(null)
            }}
          />
          {jsonError && (
            <div className="text-xs text-red-600">{jsonError}</div>
          )}
        </>
      ) : (
        <div className="rounded-md border border-gray-200 p-2 overflow-auto max-h-80">
          <ReactJson
            src={src}
            name={false}
            theme={THEME}
            collapsed={false}
            displayDataTypes={false}
            displayObjectSize={false}
            enableClipboard={true}
            onEdit={handleEdit}
            onAdd={handleAdd}
            onDelete={handleDelete}
            style={{ fontSize: "0.75rem", fontFamily: "ui-monospace, monospace" }}
          />
        </div>
      )}
    </div>
  )
}

function TextEditor({
  originalBody,
  containerRef,
}: {
  originalBody: string
  containerRef: React.RefObject<HTMLDivElement | null>
}) {
  const [value, setValue] = useState(originalBody)

  useEffect(() => {
    if (!containerRef.current) return
    dispatchBodyChange(containerRef.current, value, value !== originalBody)
  }, [value, originalBody, containerRef])

  return (
    <div ref={containerRef}>
      <textarea
        className="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs font-mono"
        rows={8}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </div>
  )
}
